/**
 * @pg/wholesale: partner (B2B) prices and the wholesale checkout.
 *
 * Only runs for logged-in customers. Asks the PetGuru wholesale API for this customer's
 * prices and, when the customer has a wholesale tier:
 *
 * - Renders the partner price on every `[data-pg-price][data-variant-id]` element (cards,
 *   product page, predictive search, cart lines), with the retail price struck through and
 *   the saving. Retail sale badges inside the same `[data-pg-card]` are hidden.
 * - Recomputes cart line totals (`[data-pg-line-total]`) and the subtotal (`[data-pg-cart-subtotal]`).
 * - Sends checkout through the API, which creates a draft order at partner prices, and hides
 *   accelerated checkout buttons that would bypass it (`[data-pg-dynamic-checkout]`).
 *
 * Prices are cached per customer in sessionStorage for 5 minutes. Re-rendered markup announces
 * itself with `pg:content:rendered` (filters, infinite scroll, variant changes) or comes from a
 * cart change (`pg:cart:changed`); both re-apply prices to the new nodes.
 *
 * API (tools/apps/api, routes/wholesale/v1/storefront.ts):
 *   GET  /wholesale/v1/storefront/prices?shopifyCustomerId=&variantIds=1,2,3
 *        → { success, data: { tierName, prices: { [variantId]: { price, originalPrice, discountPercent } } } }
 *   POST /wholesale/v1/storefront/checkout?shopifyCustomerId=  { shopifyCustomerId, items: [{ variantId, quantity }] }
 *        → { success, data: { checkoutUrl } }
 */
import { config, formatMoney, t, toast } from '@pg/core';

const API = `${config.wholesaleApiUrl}/wholesale/v1/storefront`;
const customerId = config.customerId;
const CACHE_KEY = `pg-wholesale-${customerId}`;
const CACHE_TTL = 5 * 60 * 1000;
const PARTNER_KEY = `pg-partner-${customerId}`;
const BATCH = 100;

/** @type {{ tierName: string|null, prices: Record<string, { price: number, originalPrice: number, discountPercent: number }> }} */
const state = { tierName: null, prices: {} };
/** True once the API has answered at least once on this page (cache doesn't count). */
let confirmed = false;

function readCache() {
  try {
    const cached = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
    if (cached && Date.now() - cached.ts < CACHE_TTL) return cached;
  } catch {
    /* storage blocked or corrupt: refetch */
  }
  return null;
}

function writeCache() {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), ...state }));
    // Partner status alone (it rarely changes) outlives the session: snippets/pg-scripts.liquid
    // reads it before first paint so partner-only layout (no express checkout) never jumps.
    if (state.tierName) localStorage.setItem(PARTNER_KEY, '1');
    else localStorage.removeItem(PARTNER_KEY);
  } catch {
    /* storage blocked: the next page simply refetches */
  }
}

/** Fetch prices for variants we don't know yet. Returns once all batches settle. */
async function load(variantIds) {
  const missing = [...new Set(variantIds)].filter((id) => !(id in state.prices));
  if (!missing.length) return;
  const batches = [];
  for (let i = 0; i < missing.length; i += BATCH) batches.push(missing.slice(i, i + BATCH));
  await Promise.all(
    batches.map(async (batch) => {
      const url = `${API}/prices?shopifyCustomerId=${encodeURIComponent(customerId)}&variantIds=${encodeURIComponent(batch.join(','))}`;
      try {
        const response = await fetch(url, { headers: { Accept: 'application/json' } });
        const body = await response.json();
        if (!body.success || !body.data) return;
        confirmed = true;
        state.tierName = body.data.tierName || null;
        Object.assign(state.prices, body.data.prices || {});
        // Remember "no price" too, so non-wholesale customers don't refetch on every render.
        for (const id of batch) state.prices[id] ||= { price: 0, originalPrice: 0, discountPercent: 0 };
      } catch (error) {
        console.warn('[PetGuru] Wholesale prices unavailable', error);
      }
    }),
  );
  writeCache();
}

const partnerPrice = (id) => {
  const data = state.prices[id];
  return state.tierName && data && data.discountPercent > 0 ? data : null;
};

/** Render one price element as a partner price. */
function renderPrice(el) {
  const data = partnerPrice(el.dataset.variantId);
  el.dataset.wholesaleState = 'ready';
  if (!data) return;

  const quantity = Number(el.dataset.quantity || 1);
  const current = el.querySelector('.pg-price__current');
  let compare = el.querySelector('.pg-price__compare');
  if (!compare) {
    compare = document.createElement('s');
    compare.className = 'pg-price__compare';
    // Added by us (no retail sale): hidden on cards, where it would wrap the row.
    compare.dataset.pgPartnerCompare = '';
    current?.after(compare);
  }
  if (current) current.textContent = formatMoney(Math.round(data.price * 100) * quantity);
  compare.textContent = formatMoney(Math.round(data.originalPrice * 100) * quantity);
  compare.hidden = false;

  el.classList.remove('pg-price--sale');
  el.classList.add('pg-price--partner');

  // Partner prices arrive after first paint, so nothing may change size. The partner badge
  // takes over the retail sale badge in place when there is one; on cards it otherwise joins
  // the badges over the image; on the product page it becomes a label on the price row.
  const percent = Math.round(data.discountPercent);
  const scope = el.closest('[data-pg-card]') || el.closest('.pg-product-price');
  const sale = scope?.querySelector('[data-pg-sale-badge]');
  if (scope?.querySelector('[data-pg-partner-badge]')) return;
  if (sale) {
    sale.textContent = `${t('partner_badge')} −${percent}%`;
    sale.classList.replace('pg-badge--sale', 'pg-badge--pro');
    sale.dataset.pgPartnerBadge = '';
  } else if (scope?.matches('[data-pg-card]')) {
    const badge = document.createElement('span');
    badge.className = 'pg-badge pg-badge--pro';
    badge.dataset.pgPartnerBadge = '';
    badge.textContent = `${t('partner_badge')} −${percent}%`;
    scope.querySelector('.pg-card__badges')?.append(badge);
  } else if (!el.querySelector('.pg-price__label')) {
    const label = document.createElement('span');
    label.className = 'pg-price__label';
    label.dataset.pgPartnerBadge = '';
    label.textContent = `${t('partner_price')} · −${percent}%`;
    el.append(label);
  }
}

/** Recompute cart line totals and the subtotal from partner prices. */
function renderCart(scope) {
  let subtotal = 0;
  let touched = false;
  for (const line of scope.querySelectorAll('[data-pg-cart-line]')) {
    const id = line.dataset.variantId;
    const quantity = Number(line.dataset.quantity || 1);
    const data = partnerPrice(id);
    const lineCents = data ? Math.round(data.price * 100) * quantity : Number(line.dataset.lineTotal || 0);
    subtotal += lineCents;
    if (data) {
      touched = true;
      const total = line.querySelector('[data-pg-line-total]');
      if (total) total.textContent = formatMoney(lineCents);
    }
  }
  if (!touched) return;
  for (const el of scope.querySelectorAll('[data-pg-cart-subtotal]')) el.textContent = formatMoney(subtotal);
}

/** Apply partner prices to everything inside `scope`. */
async function apply(scope = document) {
  const els = [...scope.querySelectorAll('[data-pg-price][data-variant-id]')];
  const lines = [...scope.querySelectorAll('[data-pg-cart-line]')];
  const ids = [...els.map((el) => el.dataset.variantId), ...lines.map((el) => el.dataset.variantId)].filter(Boolean);
  if (!ids.length) return;

  for (const el of els) if (!(el.dataset.variantId in state.prices)) el.dataset.wholesaleState = 'loading';
  await load(ids);
  els.forEach(renderPrice);
  renderCart(scope);
  if (state.tierName) activateCheckout();
  // A remembered partner flag that the API no longer confirms: give accelerated checkout back.
  else if (confirmed) document.documentElement.classList.remove('pg-is-partner');
}

// ── Wholesale checkout ────────────────────────────────────────────────────────

let checkoutActive = false;

function activateCheckout() {
  document.documentElement.classList.add('pg-is-partner');
  if (checkoutActive) return;
  checkoutActive = true;

  document.addEventListener(
    'submit',
    (event) => {
      const submitter = /** @type {SubmitEvent} */ (event).submitter;
      const action = /** @type {HTMLFormElement} */ (event.target).getAttribute('action') || '';
      if (!/\/cart\b/.test(action) || submitter?.getAttribute('name') !== 'checkout') return;
      event.preventDefault();
      checkout(/** @type {HTMLElement} */ (submitter));
    },
    true,
  );

  document.addEventListener(
    'click',
    (event) => {
      const link = /** @type {Element} */ (event.target).closest?.('a[href*="/checkout"]');
      if (!link) return;
      event.preventDefault();
      checkout(/** @type {HTMLElement} */ (link));
    },
    true,
  );
}

async function checkout(trigger) {
  trigger.setAttribute('aria-busy', 'true');
  try {
    const cart = await (await fetch(`${config.routes.cart}.js`)).json();
    if (!cart.items?.length) throw new Error('empty cart');
    const items = cart.items.map((item) => ({ variantId: String(item.variant_id), quantity: item.quantity }));
    const response = await fetch(`${API}/checkout?shopifyCustomerId=${encodeURIComponent(customerId)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ shopifyCustomerId: String(customerId), items }),
    });
    const body = await response.json();
    if (!body.success || !body.data?.checkoutUrl) throw new Error('no checkout url');
    location.href = body.data.checkoutUrl;
  } catch (error) {
    console.error('[PetGuru] Wholesale checkout failed', error);
    toast(t('cart_error'), { icon: 'alert' });
    trigger.removeAttribute('aria-busy');
  }
}

/** True once we know this customer is a partner (resolves after the first price load). */
export const isPartner = () => Boolean(state.tierName);
export { apply as applyWholesalePrices };

if (customerId && config.wholesaleApiUrl) {
  const cached = readCache();
  if (cached) Object.assign(state, { tierName: cached.tierName, prices: cached.prices || {} });
  if (state.tierName) document.documentElement.classList.add('pg-is-partner');

  apply();
  document.addEventListener('pg:content:rendered', (event) => apply(/** @type {Element} */ (event.target)));
  document.addEventListener('pg:cart:changed', () => apply());
}
